import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
  useRouterState,
} from '@tanstack/react-router'
import {
  Building,
  FileText,
  MessagesSquare,
  Settings,
  ShieldCheck,
  ThumbsUp,
  User,
} from 'lucide-react'
import { checkAdminAccess } from '@rpc/admin-check.rpc'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/admin')({
  beforeLoad: async () => {
    const isAdmin = await checkAdminAccess()
    if (!isAdmin) {
      throw redirect({ to: '/' })
    }
  },
  component: AdminLayout,
})

function AdminLayout() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })

  const isActivePath = (to: string, exact = false) => {
    if (exact) {
      return pathname === to || pathname === `${to}/`
    }
    return pathname === to || pathname.startsWith(`${to}/`)
  }

  const tabClassName =
    'flex items-center gap-2 border-b-2 border-transparent px-4 py-4 text-sm font-medium text-neutral-600 transition-colors hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-neutral-100'

  return (
    <div className="flex flex-col min-h-screen bg-neutral-50 dark:bg-neutral-950">
      <Header />
      <div className="border-b border-neutral-200 bg-white/95 backdrop-blur dark:border-neutral-800 dark:bg-neutral-900/90">
        <div className="mx-auto flex w-full max-w-6xl gap-1 overflow-x-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/admin"
            className={cn(
              tabClassName,
              isActivePath('/admin', true) &&
                'border-blue-500 text-blue-700 dark:text-blue-400',
            )}
          >
            <FileText className="h-4 w-4" />
            Documents
          </Link>
          <Link
            to="/admin/users"
            className={cn(
              tabClassName,
              isActivePath('/admin/users') &&
                'border-blue-500 text-blue-700 dark:text-blue-400',
            )}
          >
            <User className="h-4 w-4" />
            Users
          </Link>
          <Link
            to="/admin/conversations"
            className={cn(
              tabClassName,
              isActivePath('/admin/conversations') &&
                'border-blue-500 text-blue-700 dark:text-blue-400',
            )}
          >
            <MessagesSquare className="h-4 w-4" />
            Conversations
          </Link>
          <Link
            to="/admin/feedback"
            className={cn(
              tabClassName,
              isActivePath('/admin/feedback') &&
                'border-blue-500 text-blue-700 dark:text-blue-400',
            )}
          >
            <ThumbsUp className="h-4 w-4" />
            Feedback
          </Link>
          <Link
            to="/admin/organizations"
            className={cn(
              tabClassName,
              isActivePath('/admin/organizations') &&
                'border-blue-500 text-blue-700 dark:text-blue-400',
            )}
          >
            <Building className="h-4 w-4" />
            Organizations
          </Link>
          <Link
            to="/admin/plans"
            className={cn(
              tabClassName,
              isActivePath('/admin/plans') &&
                'border-blue-500 text-blue-700 dark:text-blue-400',
            )}
          >
            <Settings className="h-4 w-4" />
            Global plans
          </Link>
          <Link
            to="/admin/overrides"
            className={cn(
              tabClassName,
              isActivePath('/admin/overrides') &&
                'border-blue-500 text-blue-700 dark:text-blue-400',
            )}
          >
            <ShieldCheck className="h-4 w-4" />
            Overrides
          </Link>
        </div>
      </div>
      <main id="main-content" className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  )
}
